import React, { useState, useCallback, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import { SplitFlapRow } from "../../features/facility/components/queue/splitflap/QueueDisplay";
import {
  getAudioContext,
  resumeClickAudio,
  playSwoosh,
  playClickVariant,
  SWOOSH_GAIN_PCT_DEFAULT,
  SWOOSH_FREQ_MIN,
  SWOOSH_FREQ_MAX,
  SWOOSH_FREQ_DEFAULT,
} from "./splitflapAudio";
import "./SplitFlapDisplayPage.css";
import { tokens } from "@healthdesk/shared-utils";

const DEFAULT_FRAME_COLOR = tokens.splitFlapDisplayPage.frame;
const DEFAULT_CHARACTER_COLOR = "common.white";
const DEFAULT_UPPER_FLAP_COLOR = tokens.splitFlapDisplayPage.upperFlap;
const DEFAULT_LOWER_FLAP_COLOR = tokens.splitFlapDisplayPage.lowerFlap;
const DEFAULT_FLIP_DURATION_MS = 20;
const DEFAULT_STAGGER_MS = 0;
const DEFAULT_SPEED_MS = 24;

const MIN_FLIP_DURATION = 20;
const MAX_FLIP_DURATION = 250;
const MIN_STAGGER = 0;
const MAX_STAGGER = 400;
const MIN_SPEED = 20;
const MAX_SPEED = 250;

function parseClamp(str, min, max, fallback) {
  const n = parseInt(String(str).trim(), 10);
  if (Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, n));
}

/** Frequency presets for variant 6 (modal tap): non-harmonic pairs for percussive character. */
const MODAL_TAP_PRESETS = [
  { label: "Default (420 + 1180 Hz)", freq1: 420, freq2: 1180 },
  { label: "Deeper (280 + 720 Hz)", freq1: 280, freq2: 720 },
  { label: "Brighter (680 + 1650 Hz)", freq1: 680, freq2: 1650 },
  { label: "Wood (320 + 890 Hz)", freq1: 320, freq2: 890 },
  { label: "Metal (520 + 1420 Hz)", freq1: 520, freq2: 1420 },
  { label: "Soft (380 + 950 Hz)", freq1: 380, freq2: 950 },
  { label: "Low thud (200 + 540 Hz)", freq1: 200, freq2: 540 },
  { label: "High tick (900 + 2100 Hz)", freq1: 900, freq2: 2100 },
  { label: "900 + 1420 Hz", freq1: 900, freq2: 1420 },
];

const MODAL_TAP_FREQ_MIN = 100;
const MODAL_TAP_FREQ_MAX = 3000;

const SOUND_SETTINGS_STORAGE_KEY = "splitflap-display-sound-settings";

function loadSoundSettings() {
  try {
    const raw = localStorage.getItem(SOUND_SETTINGS_STORAGE_KEY);
    if (!raw) return null;
    const data = JSON.parse(raw);
    if (!data || typeof data !== "object") return null;
    return {
      soundOn: typeof data.soundOn === "boolean" ? data.soundOn : true,
      swooshOn: typeof data.swooshOn === "boolean" ? data.swooshOn : true,
      swooshGainPct: typeof data.swooshGainPct === "number" ? Math.max(0, Math.min(100, data.swooshGainPct)) : SWOOSH_GAIN_PCT_DEFAULT,
      swooshFreqHz: typeof data.swooshFreqHz === "number" ? Math.max(SWOOSH_FREQ_MIN, Math.min(SWOOSH_FREQ_MAX, data.swooshFreqHz)) : SWOOSH_FREQ_DEFAULT,
      soundVariant: typeof data.soundVariant === "number" && data.soundVariant >= 1 && data.soundVariant <= 7 ? data.soundVariant : 6,
      modalTapFreq1Hz: typeof data.modalTapFreq1Hz === "number" ? Math.max(MODAL_TAP_FREQ_MIN, Math.min(MODAL_TAP_FREQ_MAX, data.modalTapFreq1Hz)) : 600,
      modalTapFreq2Hz: typeof data.modalTapFreq2Hz === "number" ? Math.max(MODAL_TAP_FREQ_MIN, Math.min(MODAL_TAP_FREQ_MAX, data.modalTapFreq2Hz)) : 1200,
      modalTapGain1Pct: typeof data.modalTapGain1Pct === "number" ? Math.max(0, Math.min(100, data.modalTapGain1Pct)) : 7,
      modalTapGain2Pct: typeof data.modalTapGain2Pct === "number" ? Math.max(0, Math.min(100, data.modalTapGain2Pct)) : 12,
    };
  } catch (e) {
    return null;
  }
}

function saveSoundSettings(settings) {
  try {
    localStorage.setItem(SOUND_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch (e) {
    console.warn("Could not save split-flap sound settings", e);
  }
}

const DEFAULTS = {
  soundOn: true,
  swooshOn: true,
  swooshGainPct: SWOOSH_GAIN_PCT_DEFAULT,
  swooshFreqHz: SWOOSH_FREQ_DEFAULT,
  soundVariant: 6,
  modalTapFreq1Hz: 600,
  modalTapFreq2Hz: 1200,
  modalTapGain1Pct: 7,
  modalTapGain2Pct: 12,
};

const SplitFlapDisplayPage = () => {
  const [frameColor, setFrameColor] = useState(DEFAULT_FRAME_COLOR);
  const [characterColor, setCharacterColor] = useState(DEFAULT_CHARACTER_COLOR);
  const [upperFlapColor, setUpperFlapColor] = useState(DEFAULT_UPPER_FLAP_COLOR);
  const [lowerFlapColor, setLowerFlapColor] = useState(DEFAULT_LOWER_FLAP_COLOR);
  const [soundOn, setSoundOn] = useState(() => loadSoundSettings()?.soundOn ?? DEFAULTS.soundOn);
  const [swooshOn, setSwooshOn] = useState(() => loadSoundSettings()?.swooshOn ?? DEFAULTS.swooshOn);
  const [swooshGainPct, setSwooshGainPct] = useState(() => loadSoundSettings()?.swooshGainPct ?? DEFAULTS.swooshGainPct);
  const [swooshFreqHz, setSwooshFreqHz] = useState(() => loadSoundSettings()?.swooshFreqHz ?? DEFAULTS.swooshFreqHz);
  const [soundVariant, setSoundVariant] = useState(() => loadSoundSettings()?.soundVariant ?? DEFAULTS.soundVariant);
  const [modalTapFreq1Hz, setModalTapFreq1Hz] = useState(() => loadSoundSettings()?.modalTapFreq1Hz ?? DEFAULTS.modalTapFreq1Hz);
  const [modalTapFreq2Hz, setModalTapFreq2Hz] = useState(() => loadSoundSettings()?.modalTapFreq2Hz ?? DEFAULTS.modalTapFreq2Hz);
  const [showPresetModal, setShowPresetModal] = useState(false);
  const [modalTapGain1Pct, setModalTapGain1Pct] = useState(() => loadSoundSettings()?.modalTapGain1Pct ?? DEFAULTS.modalTapGain1Pct);
  const [modalTapGain2Pct, setModalTapGain2Pct] = useState(() => loadSoundSettings()?.modalTapGain2Pct ?? DEFAULTS.modalTapGain2Pct);
  const [bold, setBold] = useState(true);
  const [use3d, setUse3d] = useState(true);
  const [flipDurationInput, setFlipDurationInput] = useState(String(DEFAULT_FLIP_DURATION_MS));
  const [staggerInput, setStaggerInput] = useState(String(DEFAULT_STAGGER_MS));
  const [speedInput, setSpeedInput] = useState(String(DEFAULT_SPEED_MS));
  const [rowKey, setRowKey] = useState(0);

  const flipDurationMs = parseClamp(flipDurationInput, MIN_FLIP_DURATION, MAX_FLIP_DURATION, DEFAULT_FLIP_DURATION_MS);
  const charStaggerMs = parseClamp(staggerInput, MIN_STAGGER, MAX_STAGGER, DEFAULT_STAGGER_MS);
  const flapSpeedMs = parseClamp(speedInput, MIN_SPEED, MAX_SPEED, DEFAULT_SPEED_MS);

  const variant6Options = useMemo(
    () =>
      soundVariant === 6
        ? {
            freqs: [modalTapFreq1Hz, modalTapFreq2Hz],
            gain1Pct: modalTapGain1Pct,
            gain2Pct: modalTapGain2Pct,
          }
        : null,
    [soundVariant, modalTapFreq1Hz, modalTapFreq2Hz, modalTapGain1Pct, modalTapGain2Pct]
  );

  const swooshOptions = useMemo(
    () => ({ gainPct: swooshGainPct, startFreqHz: swooshFreqHz }),
    [swooshGainPct, swooshFreqHz]
  );

  const onFlapComplete = useCallback(
    (segmentIndex, charIndex) => {
      if (soundOn) playClickVariant(soundVariant, variant6Options);
      if (swooshOn) playSwoosh(swooshOptions);
    },
    [soundOn, soundVariant, variant6Options, swooshOn, swooshOptions]
  );

  const handleRerun = () => {
    resumeClickAudio();
    setRowKey((k) => k + 1);
  };

  const normalizeBlur = (setter, value, min, max, fallback) => {
    setter(String(parseClamp(value, min, max, fallback)));
  };

  const chartmindSegment = useMemo(() => ["CHARTMIND"], []);

  useEffect(() => {
    if (!showPresetModal) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setShowPresetModal(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [showPresetModal]);

  useEffect(() => {
    saveSoundSettings({
      soundOn,
      swooshOn,
      swooshGainPct,
      swooshFreqHz,
      soundVariant,
      modalTapFreq1Hz,
      modalTapFreq2Hz,
      modalTapGain1Pct,
      modalTapGain2Pct,
    });
  }, [soundOn, swooshOn, swooshGainPct, swooshFreqHz, soundVariant, modalTapFreq1Hz, modalTapFreq2Hz, modalTapGain1Pct, modalTapGain2Pct]);

  return (
    <div className="splitflap-display-page">
      <div className="splitflap-display-content">
        <div
          className="splitflap-display-frame"
          style={{
            borderColor: frameColor,
            backgroundColor: frameColor,
          }}
        >
          <div className="splitflap-display-display">
            <SplitFlapRow
              key={rowKey}
              textSegments={chartmindSegment}
              speeds={[flapSpeedMs]}
              rowIndex={0}
              isClock={true}
              compact={false}
              segmentBgColor="transparent"
              segmentUpperFlapColor={upperFlapColor}
              segmentLowerFlapColor={lowerFlapColor}
              characterColor={characterColor}
              fontWeight={bold ? "bold" : "normal"}
              charStaggerMs={charStaggerMs}
              flipDurationMs={flipDurationMs}
              use3dFlip={use3d}
              onFlapComplete={onFlapComplete}
              className="splitflap-display-row"
              tileWidth="clamp(52px, 7vw, 80px)"
              tileHeight="clamp(64px, 9vw, 100px)"
            />
          </div>
        </div>

        <div className="splitflap-display-params">
          <h2 className="splitflap-display-params-title">Parameters</h2>

          <div className="splitflap-display-param">
            <label>Frame color</label>
            <input
              type="color"
              value={frameColor}
              onChange={(e) => setFrameColor(e.target.value)}
              aria-label="Frame color"
            />
            <input
              type="text"
              value={frameColor}
              onChange={(e) => setFrameColor(e.target.value)}
              className="splitflap-display-hex"
              aria-label="Frame color hex"
            />
          </div>

          <div className="splitflap-display-param">
            <label>Character color</label>
            <input
              type="color"
              value={characterColor}
              onChange={(e) => setCharacterColor(e.target.value)}
              aria-label="Character color"
            />
            <input
              type="text"
              value={characterColor}
              onChange={(e) => setCharacterColor(e.target.value)}
              className="splitflap-display-hex"
              aria-label="Character color hex"
            />
          </div>

          <div className="splitflap-display-param">
            <label>Upper flap color</label>
            <input
              type="color"
              value={upperFlapColor}
              onChange={(e) => setUpperFlapColor(e.target.value)}
              aria-label="Upper flap color"
            />
            <input
              type="text"
              value={upperFlapColor}
              onChange={(e) => setUpperFlapColor(e.target.value)}
              className="splitflap-display-hex"
              aria-label="Upper flap color hex"
            />
          </div>

          <div className="splitflap-display-param">
            <label>Lower flap color</label>
            <input
              type="color"
              value={lowerFlapColor}
              onChange={(e) => setLowerFlapColor(e.target.value)}
              aria-label="Lower flap color"
            />
            <input
              type="text"
              value={lowerFlapColor}
              onChange={(e) => setLowerFlapColor(e.target.value)}
              className="splitflap-display-hex"
              aria-label="Lower flap color hex"
            />
          </div>

          <div className="splitflap-display-param splitflap-display-param-row">
            <label>Background swoosh</label>
            <label className="splitflap-display-check">
              <input
                type="checkbox"
                checked={swooshOn}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) resumeClickAudio();
                  setSwooshOn(checked);
                }}
                aria-label="Background swoosh on"
              />
              On (plays with each flap)
            </label>
            <button
              type="button"
              className="splitflap-display-try-sound"
              onClick={() => {
                const ctx = getAudioContext();
                const play = () => playSwoosh(swooshOptions);
                if (ctx && ctx.state === "suspended") ctx.resume().then(play);
                else {
                  resumeClickAudio();
                  play();
                }
              }}
              aria-label="Try swoosh"
            >
              Try swoosh
            </button>
          </div>
          <div className="splitflap-display-param splitflap-display-gain-row">
            <label>Swoosh gain</label>
            <input
              type="range"
              min={0}
              max={100}
              value={swooshGainPct}
              onChange={(e) => setSwooshGainPct(Number(e.target.value))}
              aria-label="Swoosh gain"
            />
            <span className="splitflap-display-gain-value">{swooshGainPct}%</span>
          </div>
          <div className="splitflap-display-param splitflap-display-gain-row">
            <label>Swoosh frequency (Hz)</label>
            <input
              type="range"
              min={SWOOSH_FREQ_MIN}
              max={SWOOSH_FREQ_MAX}
              step={50}
              value={swooshFreqHz}
              onChange={(e) => setSwooshFreqHz(Number(e.target.value))}
              aria-label="Swoosh frequency"
            />
            <span className="splitflap-display-gain-value">{swooshFreqHz} Hz</span>
          </div>

          <div className="splitflap-display-param splitflap-display-param-row">
            <label>Sound</label>
            <label className="splitflap-display-check">
              <input
                type="checkbox"
                checked={soundOn}
                onChange={(e) => {
                  const checked = e.target.checked;
                  if (checked) resumeClickAudio();
                  setSoundOn(checked);
                }}
                aria-label="Sound on"
              />
              On
            </label>
            <select
              value={soundVariant}
              onChange={(e) => setSoundVariant(Number(e.target.value))}
              aria-label="Sound variant"
            >
              <option value={1}>1 — High square</option>
              <option value={2}>2 — Higher square</option>
              <option value={3}>3 — Mid square</option>
              <option value={4}>4 — Thunk (filter ping)</option>
              <option value={5}>5 — Noise tick</option>
              <option value={6}>6 — Modal tap</option>
              <option value={7}>7 — Solenoid (low)</option>
            </select>
            {soundVariant === 6 && (
              <>
                <div className="splitflap-display-param splitflap-display-param-inline">
                  <button
                    type="button"
                    className="splitflap-display-preset-trigger"
                    onClick={() => setShowPresetModal(true)}
                    aria-label="Open frequency presets"
                  >
                    Presets
                  </button>
                </div>
                {showPresetModal && (
                  <div
                    className="splitflap-display-preset-modal-backdrop"
                    onClick={() => setShowPresetModal(false)}
                  >
                    <div
                      className="splitflap-display-preset-modal"
                      onClick={(e) => e.stopPropagation()}
                      role="dialog"
                      aria-label="Frequency presets"
                    >
                      <div className="splitflap-display-preset-modal-header">
                        <span>Load preset</span>
                        <button type="button" onClick={() => setShowPresetModal(false)} aria-label="Close">×</button>
                      </div>
                      <ul className="splitflap-display-preset-list">
                        {MODAL_TAP_PRESETS.map((preset, i) => (
                          <li key={i}>
                            <button
                              type="button"
                              className="splitflap-display-preset-item"
                              onClick={() => {
                                setModalTapFreq1Hz(preset.freq1);
                                setModalTapFreq2Hz(preset.freq2);
                                setShowPresetModal(false);
                              }}
                            >
                              {preset.label}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                )}
                <div className="splitflap-display-param splitflap-display-gain-row">
                  <label>Frequency 1 (Hz)</label>
                  <input
                    type="range"
                    min={MODAL_TAP_FREQ_MIN}
                    max={MODAL_TAP_FREQ_MAX}
                    step={10}
                    value={modalTapFreq1Hz}
                    onChange={(e) => setModalTapFreq1Hz(Number(e.target.value))}
                    aria-label="Frequency 1 Hz"
                  />
                  <span className="splitflap-display-gain-value">{modalTapFreq1Hz} Hz</span>
                </div>
                <div className="splitflap-display-param splitflap-display-gain-row">
                  <label>Frequency 2 (Hz)</label>
                  <input
                    type="range"
                    min={MODAL_TAP_FREQ_MIN}
                    max={MODAL_TAP_FREQ_MAX}
                    step={10}
                    value={modalTapFreq2Hz}
                    onChange={(e) => setModalTapFreq2Hz(Number(e.target.value))}
                    aria-label="Frequency 2 Hz"
                  />
                  <span className="splitflap-display-gain-value">{modalTapFreq2Hz} Hz</span>
                </div>
                <div className="splitflap-display-param splitflap-display-gain-row">
                  <label>Gain 1 ({modalTapFreq1Hz} Hz)</label>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={modalTapGain1Pct}
                    onChange={(e) => setModalTapGain1Pct(Number(e.target.value))}
                    aria-label={`Gain for ${modalTapFreq1Hz} Hz`}
                  />
                  <span className="splitflap-display-gain-value">{modalTapGain1Pct}%</span>
                </div>
                <div className="splitflap-display-param splitflap-display-gain-row">
                  <label>Gain 2 ({modalTapFreq2Hz} Hz)</label>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={modalTapGain2Pct}
                    onChange={(e) => setModalTapGain2Pct(Number(e.target.value))}
                    aria-label={`Gain for ${modalTapFreq2Hz} Hz`}
                  />
                  <span className="splitflap-display-gain-value">{modalTapGain2Pct}%</span>
                </div>
              </>
            )}
            <button
              type="button"
              className="splitflap-display-try-sound"
              onClick={() => {
                const ctx = getAudioContext();
                const play = () => playClickVariant(soundVariant, variant6Options);
                if (ctx && ctx.state === "suspended") {
                  ctx.resume().then(play);
                } else {
                  resumeClickAudio();
                  play();
                }
              }}
              aria-label="Try sound"
            >
              Try
            </button>
          </div>

          <div className="splitflap-display-param splitflap-display-param-row">
            <label>Bold</label>
            <label className="splitflap-display-check">
              <input
                type="checkbox"
                checked={bold}
                onChange={(e) => setBold(e.target.checked)}
                aria-label="Bold"
              />
              On
            </label>
          </div>

          <div className="splitflap-display-param splitflap-display-param-row">
            <label>3D animation</label>
            <label className="splitflap-display-check">
              <input
                type="checkbox"
                checked={use3d}
                onChange={(e) => setUse3d(e.target.checked)}
                aria-label="3D animation"
              />
              On
            </label>
          </div>

          <div className="splitflap-display-param">
            <label>Flip duration (ms)</label>
            <input
              type="number"
              min={MIN_FLIP_DURATION}
              max={MAX_FLIP_DURATION}
              value={flipDurationInput}
              onChange={(e) => setFlipDurationInput(e.target.value)}
              onBlur={() => normalizeBlur(setFlipDurationInput, flipDurationInput, MIN_FLIP_DURATION, MAX_FLIP_DURATION, DEFAULT_FLIP_DURATION_MS)}
              aria-label="Flip duration ms"
            />
          </div>

          <div className="splitflap-display-param">
            <label>Stagger (ms) — 0 = all change at once</label>
            <input
              type="number"
              min={MIN_STAGGER}
              max={MAX_STAGGER}
              value={staggerInput}
              onChange={(e) => setStaggerInput(e.target.value)}
              onBlur={() => normalizeBlur(setStaggerInput, staggerInput, MIN_STAGGER, MAX_STAGGER, DEFAULT_STAGGER_MS)}
              aria-label="Stagger ms (0 = all at once)"
            />
          </div>

          <div className="splitflap-display-param">
            <label>Speed / delay between flips (ms)</label>
            <input
              type="number"
              min={MIN_SPEED}
              max={MAX_SPEED}
              value={speedInput}
              onChange={(e) => setSpeedInput(e.target.value)}
              onBlur={() => normalizeBlur(setSpeedInput, speedInput, MIN_SPEED, MAX_SPEED, DEFAULT_SPEED_MS)}
              aria-label="Speed ms"
            />
          </div>

          <div className="splitflap-display-param">
            <button type="button" className="splitflap-display-rerun" onClick={handleRerun}>
              Re-run
            </button>
          </div>
        </div>
      </div>

      <p className="splitflap-display-back">
        <Link to="/splash">Back to splash</Link>
      </p>
    </div>
  );
};

export default SplitFlapDisplayPage;
